/**
 * Per-kind parameter definition editor (S10) — small controlled form inside
 * DashboardSettingsDialog (useState precedent: the dialog's own rename form).
 */
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { ConnectionSelector } from '../../../common/components/ConnectionSelector';
import type { DashboardParameter, ParamValueType } from '../../types/dashboardTypes';

type Props = {
  draft: DashboardParameter;
  siblings: DashboardParameter[];
  onCancel: () => void;
  onSave: (param: DashboardParameter) => void;
};

const VALUE_TYPES: ParamValueType[] = ['string', 'number', 'boolean', 'date'];

const Field = ({ label, children }: { label: string; children: React.ReactNode }) => (
  <div className="flex flex-col gap-1">
    <Label className="text-xs text-muted-foreground">{label}</Label>
    {children}
  </div>
);

export function ParameterEditor({ draft, siblings, onCancel, onSave }: Props) {
  const [param, setParam] = useState<DashboardParameter>(draft);
  const patch = (fields: Partial<DashboardParameter>) =>
    setParam((prev) => ({ ...prev, ...fields }) as DashboardParameter);

  const idTaken =
    param.kind !== 'time-range' &&
    siblings.some((s) => s.kind !== 'time-range' && s.id === (param as { id: string }).id);
  const idValid =
    param.kind === 'time-range' ||
    (/^[\w-]+$/.test((param as { id: string }).id) &&
      !['range', 'range_start', 'range_end', 'dashboard_filters'].includes((param as { id: string }).id));
  const canSave =
    param.kind === 'time-range'
      ? true
      : idValid &&
        !idTaken &&
        (param.kind !== 'static-list' || param.values.length > 0) &&
        (param.kind !== 'query-list' || (param.connectionId !== '' && param.sql.trim() !== ''));

  return (
    <div className="flex flex-col gap-3" data-testid="param-editor">
      {param.kind === 'time-range' ? (
        <Field label="Default window">
          <Select
            value={String(param.defaultDays)}
            onValueChange={(v) => patch({ defaultDays: Number(v) as 7 | 28 | 90 })}
          >
            <SelectTrigger className="h-7 w-32 text-xs" data-testid="param-editor-default-days">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {[7, 28, 90].map((d) => (
                <SelectItem key={d} value={String(d)}>
                  Last {d} days
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Field>
      ) : (
        <>
          <div className="grid grid-cols-2 gap-2">
            <Field label="Id (the {{token}})">
              <Input
                value={param.id}
                onChange={(e) => patch({ id: e.target.value })}
                className="h-7 font-mono text-xs"
                data-testid="param-editor-id"
                aria-invalid={!idValid || idTaken}
              />
            </Field>
            <Field label="Label">
              <Input
                value={param.label}
                onChange={(e) => patch({ label: e.target.value })}
                className="h-7 text-xs"
                data-testid="param-editor-label"
              />
            </Field>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <Field label="Value type">
              <Select
                value={param.valueType ?? 'string'}
                onValueChange={(v) => patch({ valueType: v as ParamValueType })}
              >
                <SelectTrigger className="h-7 text-xs" data-testid="param-editor-value-type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {VALUE_TYPES.map((t) => (
                    <SelectItem key={t} value={t}>
                      {t}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Default value">
              <Input
                value={
                  typeof (param as { defaultValue?: string | string[] }).defaultValue === 'string'
                    ? ((param as { defaultValue?: string }).defaultValue ?? '')
                    : ''
                }
                onChange={(e) => patch({ defaultValue: e.target.value || undefined })}
                className="h-7 text-xs"
                data-testid="param-editor-default"
              />
            </Field>
          </div>
          {(param.kind === 'static-list' || param.kind === 'query-list') && (
            <div className="flex items-center gap-4">
              <label className="flex items-center gap-1.5 text-xs">
                <Switch
                  checked={param.multi === true}
                  onCheckedChange={(multi) => patch({ multi })}
                  data-testid="param-editor-multi"
                />
                Multi-select
              </label>
              <label className="flex items-center gap-1.5 text-xs">
                <Switch
                  checked={param.includeAll === true}
                  onCheckedChange={(includeAll) => patch({ includeAll })}
                  data-testid="param-editor-include-all"
                />
                Offer “All”
              </label>
            </div>
          )}
          {param.kind === 'static-list' && (
            <Field label="Values (one per line)">
              <Textarea
                value={param.values.join('\n')}
                onChange={(e) =>
                  patch({ values: e.target.value.split('\n').map((v) => v.trim()).filter(Boolean) })
                }
                className="min-h-16 font-mono text-xs"
                data-testid="param-editor-values"
              />
            </Field>
          )}
          {param.kind === 'query-list' && (
            <>
              <Field label="Connection">
                <ConnectionSelector
                  connectionId={param.connectionId || undefined}
                  onConnectionChange={(connectionId) => patch({ connectionId })}
                  showAllEngines
                />
              </Field>
              <Field label="Options query (first two columns = label, value)">
                <Textarea
                  value={param.sql}
                  onChange={(e) => patch({ sql: e.target.value })}
                  className="min-h-16 font-mono text-xs"
                  data-testid="param-editor-sql"
                />
              </Field>
              {siblings.some((s) => s.kind !== 'time-range') && (
                <Field label="Depends on (re-fetch when these change)">
                  <div className="flex flex-wrap gap-2">
                    {siblings
                      .filter((s) => s.kind !== 'time-range')
                      .map((s) => {
                        const id = (s as { id: string }).id;
                        const checked = param.dependsOn?.includes(id) === true;
                        return (
                          <label key={id} className="flex items-center gap-1 text-xs">
                            <Switch
                              checked={checked}
                              onCheckedChange={(on) =>
                                patch({
                                  dependsOn: on
                                    ? [...(param.dependsOn ?? []), id]
                                    : (param.dependsOn ?? []).filter((d) => d !== id),
                                })
                              }
                              data-testid={`param-editor-depends-${id}`}
                            />
                            {id}
                          </label>
                        );
                      })}
                  </div>
                </Field>
              )}
            </>
          )}
        </>
      )}
      <div className="flex justify-end gap-2 border-t border-border pt-2">
        <Button variant="outline" size="sm" className="h-7 text-xs" onClick={onCancel}>
          Cancel
        </Button>
        <Button
          size="sm"
          className="h-7 text-xs"
          disabled={!canSave}
          onClick={() => onSave(param)}
          data-testid="param-editor-save"
        >
          Save parameter
        </Button>
      </div>
    </div>
  );
}
